import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Stack,
  Alert,
  styled,
} from '@mui/material';
import AnimatedSection from '../components/AnimatedSection';
import '../glow-effect.css';

// Styled components reutilizados de ContactSection
const SectionContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(8),
  paddingBottom: theme.spacing(8),
  backgroundColor: 'transparent',
  minHeight: '60vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const Title = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '2rem',
  marginBottom: theme.spacing(1),
  textAlign: 'center',
  color: '#000000',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.5rem',
  },
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  marginBottom: theme.spacing(4),
  textAlign: 'center',
  color: '#666666',
}));

const LoginCard = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.65)',
  backdropFilter: 'blur(10px)',
  borderRadius: '16px',
  padding: theme.spacing(5),
  width: '100%',
  maxWidth: '500px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.2)',
    backgroundColor: 'rgba(255, 255, 255, 0.75)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
  },
}));

const LoginCrm = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Si ya hay sesión mock, redirigir directo al dashboard
    const existingToken = localStorage.getItem('crm_token');
    if (existingToken) {
      navigate('/crm/dashboard');
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Mock de autenticación: credenciales fijas
    const validEmail = 'admin@peoplefortech.com';
    const validPassword = 'Pass1234!';

    if (email === validEmail && password === validPassword) {
      const mockToken = 'mock_token_for_demo';
      const mockUser = {
        id: 1,
        username: 'admin',
        email: validEmail,
        role: 'admin',
      };

      localStorage.setItem('crm_token', mockToken);
      localStorage.setItem('crm_user', JSON.stringify(mockUser));
      navigate('/crm/dashboard');
    } else {
      setError('Credenciales inválidas en el mockup. Usa admin@peoplefortech.com / Pass1234!');
    }

    setLoading(false);
  };

  return (
    <>
      <Helmet>
        <title>CRM Login - PeopleForTech</title>
        <meta name="description" content="Login to PeopleForTech CRM" />
      </Helmet>
      <AnimatedSection direction="bottom" delay={0.3}>
        <SectionContainer component="section">
          <Stack spacing={3} alignItems="center">
            <LoginCard>
              <Box sx={{ mb: 3 }}>
                <Title variant="h3">CRM Login</Title>
                <Subtitle variant="body1">Ingresa tus credenciales para acceder al CRM</Subtitle>
              </Box>

              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                {error && (
                  <Alert severity="error">
                    {error}
                  </Alert>
                )}

                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Tu correo electrónico"
                  variant="outlined"
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'black',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'black',
                      },
                    },
                  }}
                />

                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Tu contraseña"
                  variant="outlined"
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'black',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'black',
                      },
                    },
                  }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading}
                  className="glow-on-hover"
                  sx={{
                    py: 1.5,
                    backgroundColor: '#000000',
                    color: '#ffffff',
                    fontWeight: 600,
                    fontSize: '1rem',
                    textTransform: 'none',
                    borderRadius: 10,
                    '&:hover': {
                      backgroundColor: '#333333',
                    },
                    '&:disabled': {
                      backgroundColor: '#666666',
                    },
                  }}
                >
                  {loading ? 'Iniciando sesión...' : 'Login'}
                </Button>
              </Box>
            </LoginCard>
          </Stack>
        </SectionContainer>
      </AnimatedSection>
    </>
  );
};

export default LoginCrm;
